import { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import { useKpiIndustryBreakdownTreeV1 } from '@/hooks/database/useKpiIndustryBreakdownTreeV1';
import { logDatabaseError } from '@/utils/errorLogger';
import type { TreeMapNode, CategoryData, TreeMapRootData, WidthFunction } from '@/types/d3-treemap';

const CategoriesSectionEn = () => {
  const treemapRef = useRef<SVGSVGElement>(null);

  // Query industry data from database
  const { data: industryData, isError: industryError } = useKpiIndustryBreakdownTreeV1();

  // Use database data directly (transform to expected format)
  const categoryData: CategoryData[] = industryData?.map(item => ({
    name: item.industry,
    count: item.company_count
  })) ?? [];

  const palette = ['var(--federal-blue)', 'var(--blue)', 'var(--majorelle-blue)', 'var(--tropical-indigo)', 'var(--celestial-blue)', 'var(--carolina-blue)', 'var(--baby-blue)', 'var(--dark-cyan)', 'var(--robin-egg-blue)', 'var(--aqua)'];
  const coloredData: CategoryData[] = categoryData.map((d, i) => ({
    ...d,
    color: palette[i % palette.length]
  }));

  // Error logging in development mode
  useEffect(() => {
    if (industryError) {
      logDatabaseError('CategoriesSection', 'load industry breakdown from database', industryError);
    }
  }, [industryError]);

  useEffect(() => {
    if (!treemapRef.current || !coloredData || coloredData.length === 0) return;
    const svg = d3.select(treemapRef.current);
    svg.selectAll('*').remove();
    const width = 1200;
    const height = 600;

    // Create hierarchy with proper typing
    const rootData: TreeMapRootData = { children: coloredData };
    const hierarchy = d3.hierarchy(rootData)
      .sum((d) => ('count' in d && typeof d.count === 'number') ? d.count : 0)
      .sort((a, b) => (b.value ?? 0) - (a.value ?? 0));

    // Apply treemap layout
    const treemapLayout = d3.treemap<TreeMapRootData>()
      .size([width, height])
      .padding(3);

    const root = treemapLayout(hierarchy) as TreeMapNode<TreeMapRootData>;

    // Get leaf nodes with proper typing
    const leaves = root.leaves() as unknown as TreeMapNode<CategoryData>[];

    // Create leaf groups
    const leaf = svg
      .attr('width', width)
      .attr('height', height)
      .selectAll('g')
      .data(leaves)
      .enter()
      .append('g')
      .attr('transform', (d: TreeMapNode<CategoryData>) => `translate(${d.x0},${d.y0})`);

    // Add rectangles
    leaf
      .append('rect')
      .attr('width', (d: TreeMapNode<CategoryData>) => d.x1 - d.x0)
      .attr('height', (d: TreeMapNode<CategoryData>) => d.y1 - d.y0)
      .attr('fill', (d: TreeMapNode<CategoryData>) => d.data.color || 'var(--federal-blue)')
      .attr('stroke', 'var(--midnight-blue)')
      .attr('stroke-width', 2)
      .attr('rx', 12);

    // Add category name text with wrapping
    const widthFn: WidthFunction<CategoryData> = (d: TreeMapNode<CategoryData>) => d.x1 - d.x0 - 24;

    leaf
      .append('text')
      .attr('x', 12)
      .attr('y', 28)
      .text((d: TreeMapNode<CategoryData>) => d.data.name)
      .attr('font-size', (d: TreeMapNode<CategoryData>) => Math.min(18, (d.x1 - d.x0) / 8))
      .attr('fill', '#ffffff')
      .attr('font-weight', '700')
      .call(wrap, widthFn);

    // Add count text
    leaf
      .append('text')
      .attr('x', 12)
      .attr('y', (d: TreeMapNode<CategoryData>) => d.y1 - d.y0 - 16)
      .text((d: TreeMapNode<CategoryData>) => d.data.count.toLocaleString())
      .attr('font-size', 24)
      .attr('fill', '#ffffff')
      .attr('font-weight', '800');

    // Helper function to wrap text
    function wrap(
      text: d3.Selection<SVGTextElement, TreeMapNode<CategoryData>, SVGGElement, unknown>,
      width: WidthFunction<CategoryData>
    ) {
      text.each(function (this: SVGTextElement, d: TreeMapNode<CategoryData>) {
        const textElement = d3.select(this);
        const words = textElement.text().split(/\s+/).reverse();
        let word: string | undefined;
        let line: string[] = [];
        let lineNumber = 0;
        const lineHeight = 1.2;
        const y = textElement.attr('y');
        const dy = 0;
        let tspan = textElement
          .text(null)
          .append('tspan')
          .attr('x', 12)
          .attr('y', y)
          .attr('dy', dy + 'em');

        while ((word = words.pop()) !== undefined) {
          line.push(word);
          tspan.text(line.join(' '));
          const tspanNode = tspan.node();
          if (tspanNode && tspanNode.getComputedTextLength() > width(d)) {
            line.pop();
            tspan.text(line.join(' '));
            line = [word];
            tspan = textElement
              .append('tspan')
              .attr('x', 12)
              .attr('y', y)
              .attr('dy', ++lineNumber * lineHeight + dy + 'em')
              .text(word);
          }
        }
      });
    }
  }, [coloredData]);
  return <section className="py-24 bg-white">
      <div className="w-full px-8 md:px-16 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6 text-gray-900">Categories</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover how startups are distributed across different industry sectors and business areas
            </p>
          </div>

          {categoryData && categoryData.length > 0 ? (
            <div className="animate-on-scroll mb-12">
              <div className="text-center mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Startup Distribution by Industry
                </h3>
              </div>
              <div className="flex justify-center">
                <div className="p-8 border border-gray-600/30" style={{
                background: 'linear-gradient(135deg, var(--midnight-blue) 0%, var(--federal-blue) 50%, #000000 100%)'
              }}>
                  <svg ref={treemapRef}></svg>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center animate-on-scroll">
              <p className="text-lg text-gray-600">No industry data available yet.</p>
            </div>
          )}

          <div className="text-center animate-on-scroll">

          </div>
        </div>
      </div>
    </section>;
};
export default CategoriesSectionEn;
